import { useWebDocumentTitle } from '@/core/hooks/useWebDocumentTitle';
import { WELCOME_LOGIN_PATH } from '@/core/constants/appConfig';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import {
  guestProjectDetailProperties,
  trackCatalogEvent,
  trackGuestLoginOverlayClick,
  trackGuestProjectDetailTabClick,
  trackGuestProjectMapOpenClick,
} from '@/core/services/trackDynatraceCatalog';
import { useAppStore } from '@/core/stores/appStore';
import { withWelcomeLoginQuery } from '@/modules/bloom-universal/constants/routes';
import { markWelcomeOpenLogin } from '@/modules/welcome/utils/welcomeOpenLoginFlag';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Linking,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
  type ImageSourcePropType,
  type ScrollView,
} from 'react-native';
import { GuestFloatingLoginButton } from '../components/GuestFloatingLoginButton';
import { GuestLoginPromptDialog } from '../components/GuestLoginPromptDialog';
import {
  GuestUnitDetailsLayoutShell,
  type CompactDetailsTab,
} from '../components/GuestUnitDetailsLayoutShell';
import { GuestWebChrome } from '../components/GuestWebChrome';
import {
  getGuestDetailsLayout,
  getGuestDetailsTypeScale,
  resolveGuestChromePaddingHorizontal,
} from '../constants/guestLayout';
import { useGuestLabels } from '../hooks/useGuestLabels';
import { useGuestUnitDetails } from '../hooks/useGuestUnitDetails';

/**
 * Guest unit details (public web).
 * Compact (phone/tablet): Details | Explore tabs.
 * Desktop: side-by-side panes with independent scroll.
 */
export function GuestUnitDetailsScreen() {
  const colors = useUIColors();
  const { currentLanguage } = useTranslation();
  const labels = useGuestLabels();
  const { width } = useWindowDimensions();
  const layout = useMemo(() => getGuestDetailsLayout(width), [width]);
  const typeScale = useMemo(() => getGuestDetailsTypeScale(layout), [layout]);
  const {
    loadState,
    unit,
    exploreProperties,
    canLoadMore,
    isPrefetchingCatalogue,
    loadMore,
    handleDocumentPress,
    downloadingDocumentId,
  } = useGuestUnitDetails();
  const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);
  const [loginVisible, setLoginVisible] = useState(false);
  const [compactTab, setCompactTab] = useState<CompactDetailsTab>('details');
  const compactScrollRef = useRef<ScrollView>(null);

  const pagePaddingTop = layout.isPhone ? 16 : 24;
  /** Match guest chrome inset; WebAppLayout already insets when authenticated. */
  const pagePadX = isAuthenticated
    ? 0
    : resolveGuestChromePaddingHorizontal(width);

  const unitImages = useMemo<ImageSourcePropType[]>(
    () => (unit?.images ?? []).map((img) => ({ uri: img.uri })),
    [unit?.images],
  );

  const openLogin = useCallback(() => {
    trackGuestLoginOverlayClick(unit?.id);
    setLoginVisible(true);
  }, [unit?.id]);
  const closeLogin = useCallback(() => setLoginVisible(false), []);
  const goLogin = useCallback(() => {
    setLoginVisible(false);
    // Same as Bloom Universal hub CTA: Welcome with LoginDialog open.
    const href = withWelcomeLoginQuery(WELCOME_LOGIN_PATH);
    markWelcomeOpenLogin();
    router.push(href as never);
  }, []);

  useCatalogDynatraceView(
    DT_SCREEN.GUEST,
    'guest_project_detail_viewed',
    loadState === 'ready' && Boolean(unit),
    unit
      ? guestProjectDetailProperties({
          projectId: unit.id,
          projectName: unit.title,
          location: unit.location,
          locale: currentLanguage,
        })
      : undefined,
  );

  const openExplore = useCallback((id: string) => {
    trackCatalogEvent({
      screen: DT_SCREEN.GUEST,
      actionName: 'guest_explore_property_click',
      eventType: 'action',
      properties: { projectId: id },
    });
    router.push(`/guest-details?id=${encodeURIComponent(id)}`);
  }, []);

  const handleMapPress = useCallback(() => {
    if (unit?.latitude == null || unit?.longitude == null) {
      return;
    }
    trackGuestProjectMapOpenClick(unit.id);
    const { latitude, longitude } = unit;
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${latitude},${longitude}`,
    )}`;
    Linking.openURL(mapUrl).catch(() => {
      // Silently ignore — non-critical, external navigation only.
    });
  }, [unit]);

  const selectCompactTab = useCallback(
    (tab: CompactDetailsTab) => {
      if (unit?.id) {
        trackGuestProjectDetailTabClick(unit.id, tab);
      }
      setCompactTab(tab);
      compactScrollRef.current?.scrollTo({ y: 0, animated: false });
    },
    [unit?.id],
  );

  /** New unit from Explore → show Details tab. */
  useEffect(() => {
    setCompactTab('details');
    compactScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [unit?.id]);

  useWebDocumentTitle(
    Platform.OS === 'web' ? unit?.title : undefined,
    Platform.OS === 'web' ? unit?.description : undefined,
  );

  if (Platform.OS !== 'web') {
    return null;
  }

  const pagePadStyle = {
    paddingHorizontal: pagePadX,
    paddingTop: pagePaddingTop,
  };

  return (
    <GuestWebChrome
      headerTitle={labels.unitDetails}
      breadcrumbs={[
        { label: labels.breadcrumbHome, path: '/guest', replace: true },
        { label: labels.breadcrumbUnitDetails },
      ]}
      showBackButton
      onBackPress={() => router.replace('/guest')}
      {...(unit?.id ? { projectId: unit.id } : {})}
    >
      <View style={[styles.root, { backgroundColor: colors.backgroundLevel2 }]}>
        <GuestUnitDetailsLayoutShell
          loadState={loadState}
          unit={unit}
          unitImages={unitImages}
          exploreProperties={exploreProperties}
          canLoadMoreExplore={canLoadMore}
          isFetchingNextExplorePage={isPrefetchingCatalogue}
          onLoadMoreExplore={loadMore}
          layout={layout}
          typeScale={typeScale}
          labels={labels}
          pagePadStyle={pagePadStyle}
          compactTab={compactTab}
          compactScrollRef={compactScrollRef}
          onSelectCompactTab={selectCompactTab}
          onMapPress={handleMapPress}
          onDocumentPress={handleDocumentPress}
          downloadingDocumentId={downloadingDocumentId}
          onExplorePress={openExplore}
        />

        {!isAuthenticated ? (
          <>
            <GuestFloatingLoginButton
              onPress={openLogin}
              isPhone={layout.isPhone}
              isTablet={layout.isTablet}
            />
            <GuestLoginPromptDialog
              visible={loginVisible}
              onMaybeLater={closeLogin}
              onLogin={goLogin}
            />
          </>
        ) : null}
      </View>
    </GuestWebChrome>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
});

export default GuestUnitDetailsScreen;
